import React, { useState, useEffect, useMemo } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import Select from 'react-select';
import { useForm } from 'react-hook-form';
import { connect, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty, uniqBy } from 'lodash';
import CustomDatePicker from '../Utility/DatePicker/DatePicker';
import { fetchBomReferenceSupplier } from '../../Pages/Library/BomLibrary/store/BomLibrary.action';
import { SystemMasterSelect } from '../helper';

const initialState = {
  status: '',
  supplier: null,
  selectedDate: null,
  comment: '',
};

const LOCALLY_SOURCED = 'Locally Sourced';

/**
 * List the suppliers a single BOM may be approved against.
 *
 * A BOM names its component suppliers either directly on `BOMSupplier` or, when the order splits
 * work across product suppliers, on the component side of each `combinationSuppliers` pair.
 * "Locally Sourced" is not a real supplier — it marks a component that is not tied to one — so it
 * stands for every supplier on the order rather than for itself.
 *
 * The two sources deliberately test DIFFERENT fields for that marker, preserving behaviour this
 * screen has always had: `BOMSupplier` matches on `name`, `combinationSuppliers` on `_id`. The
 * consequence is that a real supplier record named "Locally Sourced" expands on the first path but
 * not on the second, while a legacy row storing the bare string expands on both. Unifying the two
 * would widen the dropdown on existing data, so it is left as a product decision rather than
 * folded into a bug fix.
 * @param {object} bom a BOM row from the card list
 * @param {object[]} suppliersList every supplier on the order, used to expand "Locally Sourced"
 * @returns {object[]} the suppliers this BOM contributes, duplicates included
 */
export const suppliersOnBom = (bom, suppliersList) => {
  const fromBomSupplier = !isEmpty(bom?.BOMSupplier);
  const declared = fromBomSupplier
    ? bom.BOMSupplier
    : (bom?.combinationSuppliers || []).map((combination) => combination?.componentSupplier);
  return declared.flatMap((supplier) => {
    if (!supplier) return [];
    const isLocallySourced = fromBomSupplier ? supplier.name === LOCALLY_SOURCED : supplier._id === LOCALLY_SOURCED;
    return isLocallySourced ? suppliersList || [] : [supplier];
  });
};

/**
 * Narrow the supplier dropdown to the suppliers every selected BOM carries.
 *
 * Approving a batch writes the same supplier against all of the selected BOMs, so offering a
 * supplier that only some of them carry would record an approval against a supplier who has no
 * part in that BOM. The per-BOM sets are therefore intersected, not merged, and a BOM that names
 * no supplier at all empties the result — there is nothing it can validly be approved against.
 * @param {string[]} selectedIdArray libIds of the selected BOMs
 * @param {object[]} cardList every BOM row currently loaded
 * @param {object[]} suppliersList every supplier on the order
 * @returns {object[]} the shared suppliers, de-duplicated by id
 */
export const commonSuppliers = (selectedIdArray, cardList, suppliersList) => {
  const perBom = (selectedIdArray || []).map((libId) =>
    (cardList || []).filter((bom) => bom?.libId === libId).flatMap((bom) => suppliersOnBom(bom, suppliersList)),
  );
  if (isEmpty(perBom)) return [];
  const shared = perBom.reduce((acc, list) => acc.filter((supplier) => list.some((other) => other?._id === supplier?._id)));
  return uniqBy(shared, '_id');
};

const forModalBtns = (hideSupplier) => {
  return hideSupplier ? 'status1_0' : 'status1_1';
};

const forOrignalBtns = (hideSupplier) => {
  return hideSupplier ? 'status_0' : 'status_1';
};

const fromDevId = (fromModal, status_0, hideSupplier) => {
  return fromModal ? status_0 : forOrignalBtns(hideSupplier);
};

const disablingCondition = (fromBomExpand, fromProductTesting, fromProductCareLabel, fromTestingLib, fromSampleMissingComponent, isDisable, btnsDisable) => {
  return (
    (!fromBomExpand && !fromProductTesting && !fromTestingLib && !fromProductCareLabel && !fromSampleMissingComponent && isDisable) ||
    ((fromTestingLib || fromProductTesting || fromProductCareLabel || fromBomExpand || fromSampleMissingComponent) && btnsDisable)
  );
};

function NewApproveRejectFooter({
  selectedIdArray,
  selectedIdsArray,
  devOrderId,
  selectedTab,
  hideSupplier,
  suppliersList,
  onSave,
  selectedExpand,
  disabledSaveBtn,
  userInfo,
  selectedRefCode,
  cardList,
  fromBomExpand,
  btnsDisable,
  fromProductTesting,
  fromProductCareLabel,
  fromTestingLib,
  status_0,
  status_1,
  fromModal,
  fromBomList,
  fromSampleMissingComponent,
  isDisabledState,
  fromSupplierDocumentComponent,
}) {
  const { t } = useTranslation('common');

  const ProductTestingData = useSelector((redState) => redState);
  const { UserDetails, BOMConfigDetails } = ProductTestingData;
  const { isSupplierLoggedIn } = UserDetails;
  const { isProductArchived } = BOMConfigDetails;
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();
  const [selectedDate, setDate] = useState(null);
  const [isDisable, setIsDisable] = useState(true);
  const [supplier, setSupplier] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [commentError, setCommentError] = useState(false);
  const [comment, setComment] = useState(null);

  const supplierForSelect = useMemo(() => {
    if (fromBomExpand || isEmpty(selectedIdArray)) return suppliersList || [];
    return commonSuppliers(selectedIdArray, cardList, suppliersList);
  }, [fromBomExpand, selectedIdArray, cardList, suppliersList]);

  useEffect(() => {
    if (fromBomExpand || isEmpty(selectedIdArray)) return;
    setIsDisable(fromSupplierDocumentComponent ? false : isEmpty(supplierForSelect));
  }, [fromBomExpand, selectedIdArray, supplierForSelect, fromSupplierDocumentComponent]);

  useEffect(() => {
    if (isDisable) {
      setValue('supplier', '', {
        shouldValidate: false,
      });
      setValue('status', 0, {
        shouldValidate: false,
      });
      setSupplier(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDisable]);

  const handleDropdownChange = (value) => {
    setValue('supplier', value, { shouldValidate: true });
    setSupplier(value);
  };

  const handleDateChange = (date) => {
    setDate(date);
  };

  const handleCommentData = (data) => {
    setComment(data);
    const inputLength = data.length;
    setCommentCount(inputLength);
  };
  const onSubmitMethod = async (data) => {
    if (!selectedDate || (!comment && fromProductCareLabel)) {
      if (!selectedDate) {
        setHasError(true);
      } else {
        setCommentError(true);
      }
    } else {
      !fromBomExpand && setIsDisable(disabledSaveBtn);
      const obj = {
        ...data,
        commentDate: selectedDate || data.selectedDate,
        approveBy: userInfo.userId,
      };

      if (hideSupplier) {
        const colourData =
          selectedIdArray && selectedIdArray.length > 0
            ? selectedIdArray.map((item) => ({
                supplier: item?.supplier,
                parentId: item?.parentId,
                childId: item?.childId,
                colourRefNumber: item?.colourRefNumber,
                colourRef: item?.colourRef?._id || '',
              }))
            : [];
        if (!fromProductTesting && !fromTestingLib && fromBomExpand && !fromProductCareLabel) {
          obj.refCode = selectedRefCode;
          obj.libId = selectedExpand?.libId || '';
          obj.bomId = selectedExpand?._id || '';
          obj.colourData = colourData;
        }
      } else if (!fromProductTesting && !fromProductCareLabel && !fromTestingLib && (fromBomExpand || fromBomList)) {
        obj.refCodes = selectedRefCode;
        obj.bomLibIds = selectedIdArray;
        obj.bomIds = selectedIdsArray;
        obj.supplierId = data?.supplier?._id || '';
      }
      delete obj.supplier;
      delete obj.selectedDate;
      const value = obj;

      if (fromProductTesting || fromTestingLib || fromSampleMissingComponent) {
        onSave(devOrderId, value);
        setValue('result', '');
      } else {
        onSave(selectedTab, devOrderId, value);
      }
      for (const [key, val] of Object.entries(initialState)) {
        if (key === 'selectedDate') {
          setDate(val);
          setHasError(false);
        }
        if (key === 'supplier') {
          setSupplier(val);
        }

        if (key === 'comment') {
          setComment(val);
          setCommentError(false);
        }
        setValue(key, val);
        !fromBomExpand && setIsDisable(disabledSaveBtn);
        setCommentCount(0);
      }
    }
  };

  const getPlaceholder = () => {
    if (fromSupplierDocumentComponent) {
      return t('APPROVE_REJECT_FOOTER.STATUS_COMMENTS');
    }
    if (fromBomExpand) {
      return t('APPROVE_REJECT_FOOTER.COMMENT');
    }
    return t('APPROVE_REJECT_FOOTER.COMMENT_PLACEHOLDER');
  };

  return (
    !isSupplierLoggedIn &&
    !isProductArchived && (
      <div className="new-approve-footer modal-foot">
        <div className="form w-100">
          <div className="radio-date-container radio-approve">
            <div className="approve-reject">
              <div className="d-flex align-items-center mr-16">
                <p className="custom-radio style-active-tab mb-0 ">
                  <input
                    type="radio"
                    id={fromDevId(fromModal, status_0, hideSupplier)}
                    name="status"
                    value={1}
                    {...register('status', { required: true })}
                    disabled={
                      isDisabledState ||
                      disablingCondition(
                        fromBomExpand,
                        fromProductTesting,
                        fromProductCareLabel,
                        fromTestingLib,
                        fromSampleMissingComponent,
                        isDisable,
                        btnsDisable,
                      )
                    }
                  />
                  <label className="m-0 approved-label" htmlFor={fromDevId(fromModal, status_0, hideSupplier)}>
                    {t('APPROVE_REJECT_FOOTER.APPROVED')}
                  </label>
                </p>
                <p className="custom-radio style-active-tab mb-0">
                  <input
                    type="radio"
                    id={fromModal ? status_1 : forModalBtns(hideSupplier)}
                    name="status"
                    value={0}
                    {...register('status', { required: true })}
                    disabled={
                      isDisabledState ||
                      disablingCondition(
                        fromBomExpand,
                        fromProductTesting,
                        fromProductCareLabel,
                        fromTestingLib,
                        fromSampleMissingComponent,
                        isDisable,
                        btnsDisable,
                      )
                    }
                  />
                  <label className="m-0 approved-label rejected-label" htmlFor={fromModal ? status_1 : forModalBtns(hideSupplier)}>
                    {t('APPROVE_REJECT_FOOTER.REJECTED')}
                  </label>
                </p>
                {errors.status && <span className="error-message xs-position-bottom">{t('APPROVE_REJECT_FOOTER.STATUS_REQUIRED')}</span>}
              </div>
            </div>
            <div className="date mr-16">
              <div className="input-button-container">
                <CustomDatePicker
                  valueProp={selectedDate}
                  dateFormat="dd-MMM-yyyy"
                  popperPlace="top-end"
                  name="selectedDate"
                  onChange={(date) => handleDateChange(date)}
                  disable={
                    isDisabledState ||
                    disablingCondition(
                      fromBomExpand,
                      fromProductTesting,
                      fromProductCareLabel,
                      fromTestingLib,
                      fromSampleMissingComponent,
                      isDisable,
                      btnsDisable,
                    )
                  }
                />
                {hasError && !selectedDate && <span className="error-message">{t('APPROVE_REJECT_FOOTER.DATE_REQUIRED')}</span>}
              </div>
            </div>
          </div>
          {hideSupplier ||
          fromProductTesting ||
          fromTestingLib ||
          fromProductCareLabel ||
          fromSampleMissingComponent ||
          fromSupplierDocumentComponent ? null : (
            <div className="supplier  mr-16">
              <div className="custom-react-select mb-0 common-react-select">
                <Select
                  menuPlacement="top"
                  options={supplierForSelect}
                  getOptionLabel={(option) => `${option.name}`}
                  getOptionValue={(option) => `${option._id}`}
                  name="supplier"
                  value={supplier}
                  styles={SystemMasterSelect}
                  {...register('supplier', {
                    required: true,
                  })}
                  onChange={handleDropdownChange}
                  placeholder="Select Supplier"
                  className="common-select"
                  classNamePrefix="common-select"
                  isDisabled={isDisabledState || (!fromBomExpand && isDisable) || (fromBomExpand && btnsDisable)}
                />
                {errors.supplier && <span className="error-message">{t('APPROVE_REJECT_FOOTER.SUPPLIER_SELECTION_REQUIRED')}</span>}
              </div>
            </div>
          )}

          {(fromProductTesting || fromTestingLib) && (
            <div className="note mr-16 common-input-control mb-0 approve-comment-container ">
              <input
                type="text"
                name="result"
                className="form-control approve-comment-input"
                placeholder={t('APPROVE_REJECT_FOOTER.RESULT')}
                {...register('result', {
                  required: false,
                })}
                disabled={
                  isDisabledState ||
                  disablingCondition(
                    fromBomExpand,
                    fromProductTesting,
                    fromProductCareLabel,
                    fromTestingLib,
                    fromSampleMissingComponent,
                    isDisable,
                    btnsDisable,
                  )
                }
              />
            </div>
          )}

          <div className="note-btn-container mr-16 approve-comment-container">
            <div className="note mb-0 common-input-control">
              <input
                type="text"
                name="comment"
                style={{ paddingRight: 60 }}
                className="form-control approve-comment-input"
                placeholder={getPlaceholder()}
                {...register('comment', {
                  required: false,
                })}
                maxLength={fromProductCareLabel ? '200' : '80'}
                value={comment}
                onChange={(e) => handleCommentData(e.target.value)}
                disabled={
                  isDisabledState ||
                  disablingCondition(
                    fromBomExpand,
                    fromProductTesting,
                    fromProductCareLabel,
                    fromTestingLib,
                    fromSampleMissingComponent,
                    isDisable,
                    btnsDisable,
                  )
                }
              />
              <span className="char-count">
                {commentCount}/{fromProductCareLabel ? '200' : '80'}
              </span>
              {commentError && !comment && <span className="error-message">{t('APPROVE_REJECT_FOOTER.COMMENT_REQUIRED')}</span>}
            </div>
          </div>
          <div className="button-container">
            <button
              className="btn p-0"
              onClick={handleSubmit(onSubmitMethod)}
              aria-label="save"
              type="button"
              disabled={
                isDisabledState ||
                disablingCondition(fromBomExpand, fromProductTesting, fromProductCareLabel, fromTestingLib, fromSampleMissingComponent, isDisable)
              }
            >
              <i className="f-28 icon-Save-1 text-grey" />
            </button>
          </div>
        </div>
      </div>
    )
  );
}

const mapStateToProps = ({ BomLibraryDetails, UserDetails, BOMConfigDetails }) => {
  const { userInfo } = UserDetails;
  const { ReferenceSupplier } = BomLibraryDetails;
  const { cardList } = BOMConfigDetails;
  return {
    ReferenceSupplier,
    userInfo,
    cardList,
  };
};

const mapStateToDispatch = {
  fetchBomReferenceSupplier,
};

export default connect(mapStateToProps, mapStateToDispatch)(NewApproveRejectFooter);
